// Client-side gen-worker driver (M1.2). Pre-warms chunks OFF the main thread: it
// asks the worker pool to generate chunks in range and installs the returned
// payloads into the world, so the synchronous mesher hits a cache instead of
// generating on the main thread. The world's synchronous generator remains the
// fallback, so a worker lag/failure never breaks correctness — only smoothness.
//
// Generation is fanned across a pool of workers (perf): flying an aircraft opens
// new territory ~7 chunks/second (AVI-2), which one worker can't keep generated.
import { STREAM } from '../sim/config';
import { payloadToChunk, type WorkerOutbound } from '../sim/workers/protocol';
import type { World } from '../sim/engine/world';
import { WorkerPool, poolSize, type WorkerLike } from './streaming/pool';
import { focusPoint, focusDist2, scaledBudget, speedBlocksPerTick, type Heading } from './streaming/priority';

const key = (cx: number, cz: number): string => `${cx},${cz}`;

/** Build the real gen-worker pool (browser only; sized to the machine). */
function makeGenWorkers(): Worker[] {
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined;
  const n = poolSize(cores, STREAM.GEN_WORKERS_MAX);
  const out: Worker[] = [];
  for (let i = 0; i < n; i++) {
    out.push(new Worker(new URL('../sim/workers/gen.worker.ts', import.meta.url), { type: 'module' }));
  }
  return out;
}

export class GenClient {
  private readonly pool: WorkerPool<WorkerOutbound>;
  private readonly pending = new Set<string>();
  private nextJob = 1;

  /** `workers` is injectable for tests; production builds the real pool. */
  constructor(private readonly world: World, workers?: readonly WorkerLike[]) {
    this.pool = new WorkerPool<WorkerOutbound>(workers ?? makeGenWorkers(), (d) => this.onResult(d));
    const cfg = world.genConfig;
    // Broadcast init (never transferred — the same config buffers clone into each
    // worker) so every worker generates the identical world.
    this.pool.broadcast({ type: 'init', seed: cfg.seed, flat: cfg.flat, dimension: cfg.dimension, range: cfg.range, atlas: cfg.atlas, atlasElevation: cfg.atlasElevation, earth: cfg.earth, bounds: cfg.bounds, ids: cfg.ids, blockStates: cfg.blockStates, biomes: cfg.biomes, ores: cfg.ores, vegetation: cfg.vegetation, opacityByState: cfg.opacityByState, emissionByState: cfg.emissionByState, structureStates: cfg.structureStates });
  }

  /** How many worker threads back this client (≥1). */
  get workerCount(): number { return this.pool.size; }

  private onResult(msg: WorkerOutbound): void {
    this.pending.delete(key(msg.cx, msg.cz));
    // Drop stale results: the chunk may have been generated synchronously or
    // edited while the worker was busy (E-3 / never clobber edits).
    if (this.world.hasChunk(msg.cx, msg.cz) || this.world.isEdited(msg.cx, msg.cz)) return;
    this.world.installChunk(payloadToChunk(msg));
  }

  private request(cx: number, cz: number): void {
    const k = key(cx, cz);
    if (this.pending.has(k) || this.world.hasChunk(cx, cz)) return;
    this.pending.add(k);
    this.pool.post({ type: 'gen', jobId: this.nextJob++, rev: 0, cx, cz });
  }

  /** Each frame: request the not-yet-loaded chunks within `radius` in priority
   *  order, and forget pending requests that drifted out of range. Priority and
   *  the per-frame budget follow `heading` (blocks/tick): moving fast, chunks
   *  ahead of the flight path load first and the budget grows (perf). */
  pump(pcx: number, pcz: number, radius: number, heading: Heading = { vx: 0, vz: 0 }): void {
    for (const k of this.pending) {
      const [cx, cz] = k.split(',').map(Number) as [number, number];
      if (Math.abs(cx - pcx) > radius + 1 || Math.abs(cz - pcz) > radius + 1) this.pending.delete(k);
    }
    const { fx, fz } = focusPoint(pcx, pcz, heading.vx, heading.vz, radius);
    const budget = scaledBudget(Math.max(STREAM.GEN_REQUEST_BASE, this.pool.size), speedBlocksPerTick(heading));
    const wanted: Array<{ cx: number; cz: number; d2: number }> = [];
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dz = -radius; dz <= radius; dz++) {
        const cx = pcx + dx, cz = pcz + dz;
        if (this.world.hasChunk(cx, cz) || this.pending.has(key(cx, cz))) continue;
        wanted.push({ cx, cz, d2: focusDist2(cx, cz, fx, fz) });
      }
    }
    wanted.sort((a, b) => a.d2 - b.d2);
    for (let i = 0; i < Math.min(budget, wanted.length); i++) this.request(wanted[i]!.cx, wanted[i]!.cz);
  }

  dispose(): void {
    this.pool.terminate();
  }
}
